import { createElement, useCallback, useEffect, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useShallow } from "zustand/react/shallow"

import type {
  CalculateFareResponse,
  Invoice,
  Otherpromo,
  PaymentOption,
} from "@be-miniapp/api"
import { useTranslation } from "@be-miniapp/i18n"
import { log, trackJourney } from "@be-miniapp/logging"
import { PaymentOptionKey } from "@be-miniapp/types"
import { Button } from "@be-miniapp/ui/components/ui/button"
import { useModal } from "@be-miniapp/ui/hooks/use-modal"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"
import { useToast } from "@be-miniapp/ui/hooks/use-toast"

import { useRequestRideMutation } from "../queries/use-request-ride-mutation"
import type { RideBookingVehiclePrice } from "../stores/ride-contracts"
import type { RideLocation } from "../stores/ride-data"
import {
  selectRideBooking,
  selectRideBookingActions,
  selectRideLiveSessionActions,
} from "../stores/ride-data.selectors"
import {
  buildRideBookingNavigationPayload,
  createRideBookingErrorLogData,
  createRideBookingPayload,
  shouldPromptPromoNotUsable,
} from "./ride-booking"
import { handleRideBookingError } from "./ride-booking-error-handling"
import type { RideDataStoreHook } from "./use-fare-page-model"

export interface UseRideBookingSharedDeps {
  useRideDataStore: RideDataStoreHook
  useBeUserData: (selector?: (state: unknown) => unknown) => unknown
  useUserPersistingData: <T>(
    selector: (state: { setLastSelectedVehicleType: (v: number) => void }) => T
  ) => T
}

export interface UseRideBookingSharedOptions {
  deps: UseRideBookingSharedDeps
  vehicleType: number
  isLongTrip?: boolean
  priceOfCurrentVehicle: RideBookingVehiclePrice | undefined
  currentPromo: Otherpromo | undefined
  currentVehicleFare: { can_use_promo_msg?: string } | undefined
  canUseCurrentPromo: boolean
  paymentMethod: PaymentOption | undefined
  pickup: RideLocation | undefined
  dropoff: RideLocation | undefined
  fare: CalculateFareResponse | undefined
  /** AppView uses JSON body; Zalo uses form-urlencoded */
  formUrlEncoded: boolean
  /** AppView passes fare refetch for error flows */
  refetch?: () => void
  /**
   * AppView: resolve partner/preloaded invoice. Default: use store invoice as-is.
   */
  mutateInvoiceForRequest?: (
    invoice: Invoice | undefined
  ) => Invoice | undefined
  /** Zalo: CheckoutSDK sandbox, etc. Runs after promo guard, before invalidate + mutate */
  preRequestHook?: () => void | Promise<void>
  /** AppView avoids navigating after unmount */
  enableSuccessUnmountGuard?: boolean
}

export function useRideBooking(options: UseRideBookingSharedOptions) {
  const {
    deps,
    vehicleType,
    isLongTrip,
    priceOfCurrentVehicle,
    currentPromo,
    currentVehicleFare,
    canUseCurrentPromo,
    paymentMethod,
    pickup,
    dropoff,
    fare,
    formUrlEncoded,
    refetch,
    mutateInvoiceForRequest,
    preRequestHook,
    enableSuccessUnmountGuard,
  } = options

  const { useRideDataStore, useBeUserData, useUserPersistingData } = deps

  const navigate = useNavigate()
  const modal = useModal()
  const isMounted = useRef(true)
  const requestRideRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (!enableSuccessUnmountGuard) return
    return () => {
      isMounted.current = false
    }
  }, [enableSuccessUnmountGuard])

  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { dismiss } = useToast()

  const cityId = useBeUserData((state: unknown) => {
    const s = state as { data?: { city_id?: number } } | null | undefined
    return s?.data?.city_id
  }) as number | undefined

  const {
    paymentMethod: paymentOption,
    note,
    invoice,
  } = useRideDataStore(useShallow(selectRideBooking))
  const { setCurrentPromo } = useRideDataStore(
    useShallow(selectRideBookingActions)
  )
  const { setSessionId, setCurrentState, resetRideState } = useRideDataStore(
    useShallow(selectRideLiveSessionActions)
  )

  const setLastSelectedVehicleType = useUserPersistingData(
    (state) => state.setLastSelectedVehicleType
  )
  const currentLocation = useUserPersistingData((state) => {
    const persistedState = state as {
      location?: {
        latitude?: number
        longitude?: number
      }
    }

    return persistedState.location
  }) as { latitude?: number; longitude?: number } | undefined

  const defaultPaymentMethod = PaymentOptionKey.CASH

  const showPromoGuardModal = useCallback(
    (message?: string) => {
      modal.show({
        title: t("ride.fare.error.cannot_use_promo_code.title"),
        message: message || currentVehicleFare?.can_use_promo_msg || "",
        primaryLabel: t("ride.fare.error.cannot_use_promo_code.title"),
        onPrimaryAction: (): void => {
          setCurrentPromo(undefined, true, false)
          requestRideRef.current?.()
          modal.close()
        },
        children: createElement(
          Button,
          {
            className: "w-full bg-be-navy-100",
            onClick: () => {
              modal.close()
            },
          },
          t("common.back")
        ),
        closeOnMaskClick: true,
      })
    },
    [currentVehicleFare?.can_use_promo_msg, modal, setCurrentPromo, t]
  )

  const handleBookingSuccess = useCallback(
    (resp: { session_id?: number }) => {
      if (enableSuccessUnmountGuard && !isMounted.current) return

      if (!resp?.session_id) {
        handleRideBookingError({
          error: resp,
          t,
          modal,
          navigate,
          refetch,
          onPromoNotUsable: showPromoGuardModal,
          dismiss,
        })
        return
      }

      setSessionId(resp.session_id)
      setCurrentState("finding-driver")
      setLastSelectedVehicleType(vehicleType)
      dismiss()

      navigate("/ride/in-trip", {
        state: buildRideBookingNavigationPayload({
          sessionId: resp.session_id,
          fare,
          vehicleType,
        }),
      })

      trackJourney("ride_book", "success", { vehicleType })
    },
    [
      dismiss,
      enableSuccessUnmountGuard,
      fare,
      modal,
      navigate,
      refetch,
      setCurrentState,
      setLastSelectedVehicleType,
      setSessionId,
      showPromoGuardModal,
      t,
      vehicleType,
    ]
  )

  const handleBookingError = useCallback(
    (err: unknown) => {
      const errorLogData = createRideBookingErrorLogData({
        error: err,
        vehicleType,
        cityId,
        paymentMethod,
        currentPromo,
        pickup,
        dropoff,
        isLongTrip,
        paymentOption,
        defaultPaymentMethod: defaultPaymentMethod.toString(),
      })

      log({
        level: "warning",
        message: "Request ride failed",
        data: errorLogData,
      })

      trackJourney("ride_book", "fail", {
        vehicleType,
        cityId: cityId ?? "unknown",
      })

      handleRideBookingError({
        error: err,
        t,
        modal,
        navigate,
        refetch,
        onPromoNotUsable: showPromoGuardModal,
        dismiss,
      })
    },
    [
      cityId,
      currentPromo,
      dismiss,
      dropoff,
      isLongTrip,
      modal,
      navigate,
      paymentMethod,
      paymentOption,
      pickup,
      refetch,
      showPromoGuardModal,
      t,
      vehicleType,
    ]
  )

  const { mutate: handleRequestRide, isPending: isRequestingRide } =
    useRequestRideMutation({
      buildPayload: () => {
        const invoiceForPayload = mutateInvoiceForRequest
          ? mutateInvoiceForRequest(invoice)
          : invoice

        return createRideBookingPayload({
          vehicleType,
          isLongTrip,
          currentLocation:
            currentLocation?.latitude != null &&
            currentLocation?.longitude != null
              ? {
                  latitude: currentLocation.latitude,
                  longitude: currentLocation.longitude,
                }
              : undefined,
          pickup,
          dropoff,
          priceOfCurrentVehicle,
          invoice: invoiceForPayload || undefined,
          note,
          currentPromo,
          paymentMethod,
        })
      },
      formUrlEncoded,
      onSuccess: handleBookingSuccess,
      onError: handleBookingError,
    })

  requestRideRef.current = handleRequestRide

  const beforeRequestRide = useCallback(() => {
    resetRideState()
    trackJourney("ride_book", "start", { vehicleType })

    if (shouldPromptPromoNotUsable({ canUseCurrentPromo, currentPromo })) {
      showPromoGuardModal()
      return
    }

    void preRequestHook?.()
    queryClient.invalidateQueries({
      queryKey: ["current-user-sessions"],
    })
    requestRideRef.current?.()
  }, [
    resetRideState,
    canUseCurrentPromo,
    currentPromo,
    preRequestHook,
    queryClient,
    showPromoGuardModal,
  ])

  return {
    handleRequestRide,
    isRequestingRide,
    beforeRequestRide,
    onPromoNotUsable: showPromoGuardModal,
  }
}
